import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTiltScout } from "../../hooks/useTiltScout";
import { WEEKLY_WALKTHROUGH_EVENTS } from "../../lib/weekly-walkthroughs";
import {
  ARROW_FUSE_GAP_PX,
  ARROW_SLOT_WIDTH_PX,
  FUSE_WIDTH_PX,
} from "./tilt-scout-fuse-utils";
import { normalizeTiltScoutReadLabel } from "./tilt-scout-copy";
import { TiltScoutFuseVisual } from "./TiltScoutFuseVisual";
import { TiltScoutPanel } from "./TiltScoutStrip";

interface TiltScoutHeaderFuseProps {
  selectedSymbol: string;
}

const TILT_SCOUT_PANEL_CLOSE_GRACE_MS = 140;
const TILT_SCOUT_PANEL_FADE_OUT_MS = 150;

export function TiltScoutHeaderFuse({
  selectedSymbol,
}: TiltScoutHeaderFuseProps) {
  const { snapshot, refresh, isLoading, error } = useTiltScout(selectedSymbol);
  const fuseRef = useRef<HTMLButtonElement>(null);
  const openFrameRef = useRef<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const readLabel = normalizeTiltScoutReadLabel(snapshot.label);

  const clearCloseTimers = useCallback(() => {
    if (openFrameRef.current !== null) {
      window.cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
  }, []);

  const openPanel = useCallback(() => {
    clearCloseTimers();
    const rect = fuseRef.current?.getBoundingClientRect();
    if (rect) {
      const left = Math.min(
        window.innerWidth - 376,
        Math.max(8, rect.right - 360),
      );
      setPanelPosition({ top: rect.bottom + 7, left });
    }
    setOpen(true);
    setClosing(false);
    openFrameRef.current = window.requestAnimationFrame(() => {
      openFrameRef.current = window.requestAnimationFrame(() => {
        setVisible(true);
        openFrameRef.current = null;
      });
    });
  }, [clearCloseTimers]);

  const scheduleClose = useCallback(() => {
    if (openFrameRef.current !== null) {
      window.cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
    }
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setVisible(false);
      setClosing(true);
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = setTimeout(() => {
        setOpen(false);
        setClosing(false);
        unmountTimerRef.current = null;
      }, TILT_SCOUT_PANEL_FADE_OUT_MS);
      closeTimerRef.current = null;
    }, TILT_SCOUT_PANEL_CLOSE_GRACE_MS);
  }, []);

  useEffect(() => () => clearCloseTimers(), [clearCloseTimers]);

  useEffect(() => {
    const focusTiltScout = () => {
      fuseRef.current?.focus({ preventScroll: true });
      openPanel();
    };
    const params = new URLSearchParams(window.location.search);
    const requested =
      params.get("weeklyWalkthrough") ??
      params.get("walkthrough") ??
      params.get("demo");
    if (requested === "equities-sentiment") {
      window.setTimeout(focusTiltScout, 0);
    }
    window.addEventListener(
      WEEKLY_WALKTHROUGH_EVENTS.equitiesSentimentFocus,
      focusTiltScout,
    );
    return () =>
      window.removeEventListener(
        WEEKLY_WALKTHROUGH_EVENTS.equitiesSentimentFocus,
        focusTiltScout,
      );
  }, [openPanel]);

  return (
    <div className="relative flex h-7 items-center">
      <button
        ref={fuseRef}
        type="button"
        data-testid="tiltscout-header-fuse"
        data-weekly-walkthrough-target="equities-sentiment"
        className="grid h-8 items-center px-0 text-[var(--fintheon-muted)]/45 transition-opacity duration-150 hover:opacity-100 focus-visible:outline-none"
        style={{
          columnGap: ARROW_FUSE_GAP_PX,
          gridTemplateColumns: `${ARROW_SLOT_WIDTH_PX}px ${FUSE_WIDTH_PX}px ${ARROW_SLOT_WIDTH_PX}px`,
          width:
            FUSE_WIDTH_PX + ARROW_SLOT_WIDTH_PX * 2 + ARROW_FUSE_GAP_PX * 2,
        }}
        aria-label={`TiltScout ${snapshot.canonicalSymbol} ${readLabel}`}
        title={`TiltScout ${snapshot.canonicalSymbol}: ${readLabel}`}
        onMouseEnter={openPanel}
        onMouseLeave={scheduleClose}
        onFocus={openPanel}
        onBlur={scheduleClose}
        onClick={openPanel}
      >
        <TiltScoutFuseVisual
          score={snapshot.score}
          posture={snapshot.posture}
        />
      </button>
      {open &&
        createPortal(
          <div
            className={`t-dropdown fixed z-[9999] w-[360px] [--t-dropdown-close-dur:150ms] [--t-dropdown-open-dur:180ms] ${
              closing ? "is-closing" : visible ? "is-open" : ""
            }`}
            data-origin="top-right"
            style={{ top: panelPosition.top, left: panelPosition.left }}
            onMouseEnter={openPanel}
            onMouseLeave={scheduleClose}
          >
            <TiltScoutPanel
              snapshot={snapshot}
              refresh={refresh}
              isLoading={isLoading}
              error={error}
              className="shadow-none"
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
